/**
 * Customer 360 v7 (Part D §5; CORE-2): the side cards and the tab bodies without sub-tabs.
 *   · ContactCard: the phone masked, "Show number" through the logged reveal (a reason is asked), WhatsApp, language,
 *     preferred channel, consents, e-mail — edited in place (api.customer_contact / update_customer_contact);
 *   · Sections: a tab's parts one under another, each shown only to someone who may see it (no sub-tabs);
 *   · AcquisitionCard: where the customer came from (source, referral, who referred, how the record was made).
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Pencil } from 'lucide-react';
import { rpc } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { Button, ErrorState, LoadingRows, Panel, PanelHeader, plainError } from '../../ui/primitives';
import { Checkbox, Field, Input, Select } from '../../ui/forms';
import { toast } from '../../ui/toast';
import * as f from '../../ui/format';
import { ConfirmDialog } from '../../ui/overlays';

interface Contact {
  phone: string | null; altPhone: string | null; email: string | null; whatsapp: boolean | null; language: string | null; preferredChannel: string | null;
  consentWhatsapp: boolean; consentEmail: boolean; source: string | null; referral: string | null; origin: string | null; since: string | null;
  referredBy: { id: string; code: string; name: string } | null; mayReveal: boolean; mayEdit: boolean;
}
const LANG: Record<string, string> = { hi: 'Hindi', hinglish: 'Hinglish', en: 'English' };
const CHANNEL: Record<string, string> = { CALL: 'Call', WHATSAPP: 'WhatsApp', SMS: 'SMS', EMAIL: 'E-mail' };
const ORIGIN: Record<string, string> = { DESK: 'Made at the desk', IMPORT_FRESH: 'Imported (new)', IMPORT_UAT_OLD_ERP: 'Imported from the old ERP', KHATA: 'From the Khata', FLEET_V1: 'From the old delivery app' };

function useContact(customerId: string) {
  return useQuery({ queryKey: ['customer-contact', customerId], queryFn: () => rpc<Contact>('customer_contact', { p_customer_id: customerId }) });
}

export function ContactCard({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const q = useContact(customerId);
  const [revealed, setRevealed] = useState<{ phone: string | null; altPhone: string | null; email: string | null } | null>(null);
  const [editing, setEditing] = useState<Partial<Contact> | null>(null);
  const [asking, setAsking] = useState(false);
  const reveal = useMutation({
    mutationFn: (reason: string) => rpc<{ phone: string | null; altPhone: string | null; email: string | null }>('reveal_customer_contact', { p_customer_id: customerId, p_reason: reason }),
    onSuccess: (r) => setRevealed(r),
  });
  const save = useMutation({
    mutationFn: (patch: Partial<Contact>) => rpc<Contact>('update_customer_contact', { p_customer_id: customerId, p_patch: {
      whatsapp: patch.whatsapp, language: patch.language ?? '', preferredChannel: patch.preferredChannel ?? '', email: patch.email ?? '',
      consentWhatsapp: patch.consentWhatsapp, consentEmail: patch.consentEmail }, p_reason: 'Contact details changed on the customer page' }),
    onSuccess: () => { toast('Saved.', { tone: 'ok' }); setEditing(null); void qc.invalidateQueries({ queryKey: ['customer-contact', customerId] }); },
  });
  const c = q.data;
  const ask = () => setAsking(true);
  return (
    <Panel>
      <PanelHeader title="Contact" actions={c?.mayEdit && !editing ? <Button size="dense" onClick={() => setEditing({ ...c, email: '' })}><Pencil size={13} aria-hidden /> Edit</Button> : undefined} />
      {q.error ? <ErrorState error={q.error} /> : !c ? <LoadingRows rows={3} columns={2} /> : editing ? (
        <div className="grid gap-3 px-4 py-3">
          <Field label="Language"><Select value={editing.language ?? ''} onChange={(e) => setEditing({ ...editing, language: e.target.value })} placeholder="Not given"
            options={Object.entries(LANG).map(([value, label]) => ({ value, label }))} /></Field>
          <Field label="Reach them by"><Select value={editing.preferredChannel ?? ''} onChange={(e) => setEditing({ ...editing, preferredChannel: e.target.value })} placeholder="Not given"
            options={Object.entries(CHANNEL).map(([value, label]) => ({ value, label }))} /></Field>
          <Field label="New e-mail" hint="Leave empty to keep it as it is"><Input type="email" value={editing.email ?? ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></Field>
          <Checkbox checked={!!editing.whatsapp} onChange={(e) => setEditing({ ...editing, whatsapp: e.target.checked })} label="The mobile has WhatsApp" />
          <Checkbox checked={!!editing.consentWhatsapp} onChange={(e) => setEditing({ ...editing, consentWhatsapp: e.target.checked })} label="Agrees to WhatsApp messages" />
          <Checkbox checked={!!editing.consentEmail} onChange={(e) => setEditing({ ...editing, consentEmail: e.target.checked })} label="Agrees to e-mails" />
          {save.error ? <p role="alert" className="text-small text-refused">{plainError((save.error as Error).message)}</p> : null}
          <div className="flex justify-end gap-2"><Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate({ ...editing, email: editing.email || undefined })}>Save</Button></div>
        </div>
      ) : (
        <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-4 py-3 text-small">
          <dt className="text-ink-muted">Mobile</dt>
          <dd className="tabular">{revealed?.phone ?? c.phone ?? '—'}
            {/* 139500: the whole number comes already; Show stays only while something (the e-mail) is still masked. */}
            {!revealed && c.mayReveal && [c.phone, c.altPhone, c.email].some((x) => x?.includes('•')) ? <button type="button" onClick={ask} className="ml-2 text-brand hover:underline"><Eye size={12} className="mr-0.5 inline" aria-hidden />Show</button> : null}</dd>
          {c.altPhone ? <><dt className="text-ink-muted">Second</dt><dd className="tabular">{revealed?.altPhone ?? c.altPhone}</dd></> : null}
          <dt className="text-ink-muted">WhatsApp</dt><dd>{c.whatsapp === null ? '—' : c.whatsapp ? 'Yes' : 'No'}{c.consentWhatsapp ? ' · agrees to messages' : ''}</dd>
          <dt className="text-ink-muted">Language</dt><dd>{c.language ? LANG[c.language] : '—'}</dd>
          <dt className="text-ink-muted">Reach by</dt><dd>{c.preferredChannel ? CHANNEL[c.preferredChannel] : '—'}</dd>
          <dt className="text-ink-muted">E-mail</dt><dd>{revealed?.email ?? c.email ?? '—'}{c.email && c.consentEmail ? ' · agrees' : ''}</dd>
        </dl>)}
      {reveal.error ? <p role="alert" className="px-4 pb-3 text-small text-refused">{plainError((reveal.error as Error).message)}</p> : null}
      <ConfirmDialog open={asking} onClose={() => setAsking(false)} title="Show the full number" confirmLabel="Show" requireReason
        consequence="The full number and e-mail are personal data. Who looked, when and why is recorded on the customer's history."
        onConfirm={async (reason) => { await reveal.mutateAsync(reason ?? ''); }} />
    </Panel>
  );
}

export function AcquisitionCard({ customerId }: { customerId: string }) {
  const q = useContact(customerId);
  const qc = useQueryClient();
  const { can } = useSession();
  const may = can('customer.edit');
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => rpc<{ detail: string }>('set_customer_referred_by', { p_customer_id: customerId, p_referrer_code: code.trim() || null, p_reason: reason.trim() }),
    onSuccess: (r) => { toast(r.detail, { tone: 'ok' }); setEditing(false); void qc.invalidateQueries({ queryKey: ['customer-contact', customerId] }); },
  });
  const c = q.data;
  if (!c || (!may && !c.source && !c.referral && !c.referredBy && !c.origin && !c.since)) return null;
  return (
    <Panel>
      <PanelHeader title="Where they came from" actions={may && !editing ? <Button size="dense" onClick={() => { setEditing(true); setCode(c.referredBy?.code ?? ''); setReason(''); save.reset(); }}><Pencil size={13} aria-hidden /> Referred by</Button> : undefined} />
      {editing ? (
        <div className="grid gap-2 px-4 py-3 text-small">
          <Field label="Referred by (customer code)" hint="Empty: nobody referred them"><Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="DSG-JDH-00123" /></Field>
          <Field label="Why"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. told us when joining" /></Field>
          {save.error ? <p role="alert" className="text-refused">{plainError((save.error as Error).message)}</p> : null}
          <div className="flex justify-end gap-2"><Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button variant="primary" disabled={save.isPending || reason.trim().length < 3} onClick={() => save.mutate()}>Save</Button></div>
          <p className="text-xs text-ink-muted">"Heard of us" and the referral note are edited from Contact › Edit.</p>
        </div>
      ) : (
      <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-4 py-3 text-small">
        {c.source ? <><dt className="text-ink-muted">Heard of us</dt><dd>{c.source}</dd></> : null}
        <dt className="text-ink-muted">Referred by</dt><dd>{c.referredBy ? <Link className="hover:text-brand" to={`/customers/${c.referredBy.id}`}>{c.referredBy.name} · {c.referredBy.code}</Link> : '—'}</dd>
        {c.referral ? <><dt className="text-ink-muted">Referral</dt><dd>{c.referral}</dd></> : null}
        {c.origin ? <><dt className="text-ink-muted">Record</dt><dd>{ORIGIN[c.origin] ?? c.origin}</dd></> : null}
        {c.since ? <><dt className="text-ink-muted">Customer since</dt><dd>{f.date(c.since)}</dd></> : null}
      </dl>)}
    </Panel>
  );
}

/** A tab's parts, one under another; a part someone may not see is left out (v7: no sub-tabs). */
export function Sections({ parts }: { parts: { key: string; permission?: string; anyOf?: string[]; node: ReactNode }[] }) {
  const { can } = useSession();
  const shown = parts.filter((p) => (!p.permission || can(p.permission)) && (!p.anyOf || p.anyOf.some((x) => can(x))));
  return <div className="grid gap-4">{shown.map((p) => <div key={p.key} className="min-w-0">{p.node}</div>)}</div>;
}

interface Missed { date: string; variant: string; units: number; round: string; reason: string | null; note: string | null; charged: boolean; madeUp: boolean; taskOpen: boolean }

/** Missed deliveries of the last 30 days (v7 CORE-6): each failed door, why, never charged, and whether it was made up. */
export function MissedDeliveriesCard({ customerId }: { customerId: string }) {
  const q = useQuery({ queryKey: ['customer-missed', customerId], queryFn: () => rpc<Missed[]>('customer_missed_deliveries', { p_customer_id: customerId, p_days: 30 }) });
  if (!q.data || q.data.length === 0) return null;
  return (
    <Panel className="border-refused">
      <PanelHeader title="Missed deliveries" note="Last 30 days · not charged" />
      <ul className="grid gap-1.5 px-4 py-3 text-small">
        {q.data.map((m, i) => (
          <li key={i}>
            <span className="font-medium">{f.date(m.date)}</span> · {m.units} {m.variant} · {m.reason ?? 'no reason'}{m.note ? ` — ${m.note}` : ''}
            <span className="text-ink-muted"> · {m.madeUp ? 'made up' : m.taskOpen ? 'CRM task open' : 'round ' + m.round}</span>
          </li>))}
      </ul>
      <p className="px-4 pb-3 text-small text-ink-muted">To make it up: Change delivery › Make-up delivery.</p>
    </Panel>
  );
}
